// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TabGroup, { TabGroupPresentationProps } from "@adl/MobiusOverrides/TabGroup/TabGroup";
import defaultProps from "@adl/XDL/PreStyle/tab";
import { avoidBreakInsidePrintElement, color, xScreen } from "@adl/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    tabsWrapper?: InjectableCss;
    tabsGroup?: TabGroupPresentationProps;
    tabContainer?: TabPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeContent?: InjectableCss;
    attributeType?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    printTitle?: TypographyPresentationProps;
    specificationsWrapper?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    tabsWrapper: {
        css: css`
            margin-bottom: 48px;
            ${avoidBreakInsidePrintElement}
            ${xScreen("lte", "md")} {
                margin-bottom: 52px;
            }
            &:has(> div[data-test-selector="productDetails_htmlContent"]:empty) {
                margin-bottom: 0;
            }
        `,
    },
    tabsGroup: {
        cssOverrides: {
            tabGroup: [
                defaultProps.groupDefaultProps?.cssOverrides?.tabGroup,
                css`
                    @media print {
                        display: none;
                    }
                `,
            ],
            tabContent: [
                defaultProps.groupDefaultProps?.cssOverrides?.tabContent,
                css`
                    padding: 32px 0 24px;
                `,
            ],
        },
    },
    tabContainer: {
        css: css`
            ${getProp("theme.tab.defaultProps.css")};

            @media print {
                display: none;
            }
        `,
    },
    documentsContainer: {
        gap: 25,
        css: css`
            padding: 20px;
            ${xScreen("lte", "md")} {
                padding: 20px 0;
            }
        `,
    },
    documentGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    attributeTypeLabelText: {
        size: 15,
        weight: "bold",
        css: css`
            flex: 1 1 0%;
            margin-right: 16px;
            text-align: right;
        `,
    },
    attributeTypeValuesText: {
        size: 15,
        css: css`
            flex: 1 1 0%;
            margin-left: 16px;
        `,
    },
    documentLink: {
        icon: {
            iconProps: {
                src: File,
            },
        },
        typographyProps: {
            size: 15,
            weight: "bold",
        },
    },
    printTitle: {
        size: 18,
        weight: "bold",
        color: "text",
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            margin-bottom: 0;
            @media not print {
                display: none;
            }
        `,
    },
    attributeTypeWrapper: {
        css: css`
            padding: 5px 0;
            display: flex;
        `,
    },
    attributeTypeContent: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    attributeType: {
        css: css`
            flex: 0 1 calc(50% - 11px);
            padding: 0 12px;
            &:nth-child(4n + 1),
            &:nth-child(4n + 2) {
                background: ${color("common.ui12")};
            }
            &:nth-child(4n + 1),
            &:nth-child(4n + 3) {
                margin: 0 22px 0 0;
            }
            ${xScreen("lte", "sm")} {
                flex: 1 1 100%;

                &:nth-child(4n + 1),
                &:nth-child(4n + 3) {
                    margin: 0;
                }

                &:nth-child(4n + 2) {
                    background: ${color("common.white")};
                }
                &:nth-child(4n + 3) {
                    background: ${color("common.ui12")};
                }
            }

            @media print {
                break-inside: avoid;
                page-break-inside: avoid;
                column-break-inside: avoid;
            }
        `,
    },
    specificationsWrapper: {
        css: css`
            dl {
                display: flex;
                flex-wrap: wrap;
                margin: 0;
                width: calc(100% + 22px);
                dt {
                    text-align: right;
                    margin: 0;
                    padding: 7px 32px;
                    font-weight: bold;
                    line-height: 26px;
                    &:nth-child(8n + 1),
                    &:nth-child(8n + 3) {
                        background: ${color("common.ui11")};
                        & + dd {
                            background: ${color("common.ui11")};
                        }
                    }

                    ${xScreen("lte", "sm")} {
                        &:nth-child(8n + 1),
                        &:nth-child(8n + 3) {
                            background: ${color("common.white")};
                            & + dd {
                                background: ${color("common.white")};
                            }
                        }

                        &:nth-child(4n + 1) {
                            background: ${color("common.ui11")};
                            & + dd {
                                background: ${color("common.ui11")};
                            }
                        }
                    }
                }

                dd {
                    margin: 0 22px 0 0;
                    padding: 7px 32px;
                    &:nth-child(4n) {
                        margin: 0;
                    }
                }

                dt,
                dd {
                    flex: 0 0 calc(25% - 11px);
                    ${xScreen("lte", "sm")} {
                        flex: 0 0 calc(50% - 11px);
                    }
                }
            }
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const documentsTab = renderingDocuments && (
        <Tab
            headline={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            tabKey="documents-tab"
            key="documents"
            {...styles.tabContainer}
        >
            <Typography {...styles.printTitle}>{translate("Documents")}</Typography>
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <Typography {...styles.documentNameText}>{document.description ? document.description : document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </Tab>
    );

    const attributesTab = renderingAttributes && (
        <Tab
            headline={translate("Attributes")}
            data-test-selector="productDetails_specifications_attributes"
            tabKey="attributes-tab"
            key="attributes"
            {...styles.tabContainer}
        >
            <Typography {...styles.printTitle}>{translate("Attributes")}</Typography>
            <StyledWrapper {...styles.attributeTypeContent}>
                {/* {brand && (
                    <StyledWrapper {...styles.attributeType}>
                        <StyledWrapper {...styles.attributeTypeWrapper}>
                            <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}</Typography>
                            <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )} */}
                {attributeTypes &&
                    attributeTypes.map(attributeType => (
                        <StyledWrapper key={attributeType.id.toString()} {...styles.attributeType}>
                            <StyledWrapper {...styles.attributeTypeWrapper}>
                                <Typography {...styles.attributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}
                                </Typography>
                                <Typography {...styles.attributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
            </StyledWrapper>
        </Tab>
    );

    const specificationTabs =
        renderingSpecifications &&
        specifications?.map(specification => (
            <Tab
                headline={translate(specification.nameDisplay || specification.name || "Specifications")}
                data-test-selector="productDetails_specifications_specification"
                key={specification.id.toString()}
                tabKey={`${specification.id.toString() + specification.nameDisplay.toLowerCase()}-tab`}
                {...styles.tabContainer}
            >
                <Typography {...styles.printTitle}>
                    {translate(specification.nameDisplay || specification.name || "Specifications")}
                </Typography>
                <StyledWrapper {...styles.specificationsWrapper}>
                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                </StyledWrapper>
            </Tab>
        ));

    const tabs = [];

    // Xng-Change
    // Show specifications tab first
    if (specificationTabs) {
        tabs.push(...specificationTabs);
    }
    if (attributesTab && attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && documentsFirst) {
        tabs.push(documentsTab);
    }
    if (attributesTab && !attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && !documentsFirst) {
        tabs.push(documentsTab);
    }

    return (
        <StyledWrapper {...styles.tabsWrapper}>
            <TabGroup
                data-test-selector="productDetails_specifications"
                selectLabel={translate("Product Information")}
                {...styles.tabsGroup}
            >
                {tabs}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
